/**
 * Permission keys and the system roles that bundle them.
 *
 * This file is the single source of truth for RBAC keys: the API seeds the
 * `permissions`/`roles` tables from it and checks routes against it; the web
 * app uses it to show or hide UI. Add a permission here, then re-run the seed.
 * Naming: `resource.action[.scope]`.
 */
export const PERMISSIONS = {
  USERS_READ: "users.read",
  USERS_MANAGE: "users.manage",
  ROLES_MANAGE: "roles.manage",
  ORG_MANAGE: "org.manage",
  AUDIT_READ: "audit.read",
  CONFIG_MANAGE: "config.manage",
  IMPORTS_MANAGE: "imports.manage",
  REQUESTS_CREATE: "requests.create",
  REQUESTS_READ_OWN: "requests.read.own",
  REQUESTS_READ_SCOPED: "requests.read.scoped",
  REQUESTS_READ_ALL: "requests.read.all",
  REQUESTS_REVIEW: "requests.review",
  REQUESTS_ASSIGN: "requests.assign",
  USAGE_READ: "usage.read",
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS: readonly PermissionKey[] = Object.values(PERMISSIONS);

export const PERMISSION_DESCRIPTIONS: Record<PermissionKey, string> = {
  "users.read": "View users and their roles",
  "users.manage": "Create, invite, deactivate users and assign roles",
  "roles.manage": "Create and edit roles and their permissions",
  "org.manage": "Manage legal entities and business units",
  "audit.read": "Read the audit log",
  "config.manage": "Manage application configuration",
  "imports.manage": "Run and review data imports",
  "requests.create": "Start purchase requests",
  "requests.read.own": "Read own purchase requests",
  "requests.read.scoped": "Read requests within assigned legal entities / business units",
  "requests.read.all": "Read all requests",
  "requests.review": "Review, return and decide requests",
  "requests.assign": "Assign requests to buyers",
  "usage.read": "View usage, cost and latency (aggregates only)",
};

/** Where a role assignment applies. See ADR-0012. */
export const SCOPE_TYPES = ["GLOBAL", "LEGAL_ENTITY", "BUSINESS_UNIT"] as const;
export type ScopeType = (typeof SCOPE_TYPES)[number];

export const SYSTEM_ROLE_KEYS = {
  REQUESTER: "requester",
  BUYER: "buyer",
  PROCUREMENT_MANAGER: "procurement_manager",
  ADMIN: "admin",
} as const;

export type SystemRoleKey = (typeof SYSTEM_ROLE_KEYS)[keyof typeof SYSTEM_ROLE_KEYS];

export interface SystemRoleDefinition {
  key: SystemRoleKey;
  name: string;
  description: string;
  permissions: readonly PermissionKey[];
  /**
   * Scope types this role may be granted with (ADR-0012). Buyers work only inside
   * assigned legal entities / business units; the other roles are organisation-wide
   * (a requester's own requests and an admin's user management are not scoped).
   */
  scopeTypes: readonly ScopeType[];
}

const P = PERMISSIONS;

export const SYSTEM_ROLES: readonly SystemRoleDefinition[] = [
  {
    key: "requester",
    name: "Requester",
    description: "Raises purchase requests and sees their own.",
    permissions: [P.REQUESTS_CREATE, P.REQUESTS_READ_OWN],
    scopeTypes: ["GLOBAL"],
  },
  {
    key: "buyer",
    name: "Buyer",
    description: "Works requests in assigned legal entities / business units.",
    permissions: [P.REQUESTS_CREATE, P.REQUESTS_READ_OWN, P.REQUESTS_READ_SCOPED, P.REQUESTS_REVIEW],
    scopeTypes: ["LEGAL_ENTITY", "BUSINESS_UNIT"],
  },
  {
    key: "procurement_manager",
    name: "Procurement manager",
    description: "Oversees all requests, triages and assigns buyers.",
    permissions: [
      P.REQUESTS_CREATE,
      P.REQUESTS_READ_OWN,
      P.REQUESTS_READ_ALL,
      P.REQUESTS_REVIEW,
      P.REQUESTS_ASSIGN,
      P.USERS_READ,
      P.AUDIT_READ,
      P.IMPORTS_MANAGE,
    ],
    scopeTypes: ["GLOBAL"],
  },
  {
    key: "admin",
    name: "Administrator",
    description: "Manages users, roles, organisation and configuration.",
    permissions: ALL_PERMISSIONS,
    scopeTypes: ["GLOBAL"],
  },
];
